import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/helpers';
import type { Article } from '../types';

interface ArticleTableProps {
  articles: Article[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  error: string;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  pendingArticleId: number | null;
  onPageChange: (page: number) => void;
  onView: (article: Article) => void;
  onCreate: () => void;
  onEdit: (article: Article) => void;
  onDelete: (article: Article) => void;
  onRetry: () => void;
}

const safeFormatDate = (value: string | null): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const ArticleTable: React.FC<ArticleTableProps> = ({
  articles,
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  isLoading,
  isInitialLoading,
  error,
  canCreate,
  canUpdate,
  canDelete,
  pendingArticleId,
  onPageChange,
  onView,
  onCreate,
  onEdit,
  onDelete,
  onRetry,
}) => {
  const { t } = useTranslation();
  const firstItem = totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, totalElements);

  return (
    <div className="relative flex min-h-[420px] flex-1 flex-col">
      {isLoading && !isInitialLoading && (
        <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.articles.loading')}>
          <div className="h-full w-1/2 animate-pulse bg-primary" />
        </div>
      )}

      <div className="flex-1 overflow-x-auto">
        <table className={`w-full min-w-[980px] border-collapse text-left text-body-sm transition-opacity ${isLoading && !isInitialLoading ? 'opacity-60' : ''}`}>
          <thead className="bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              {['article', 'content', 'created_at', 'updated_at', 'actions'].map((column) => (
                <th key={column} className={`px-5 py-3 font-label-sm text-label-sm text-secondary ${column === 'actions' ? 'text-right' : ''}`}>
                  {t(`admin.articles.table.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">
            {isInitialLoading && Array.from({ length: 6 }, (_, index) => (
              <tr key={index}>
                <td colSpan={5} className="px-5 py-3">
                  <div className="h-14 animate-pulse rounded-xl bg-surface-container-low" />
                </td>
              </tr>
            ))}

            {!isInitialLoading && error && articles.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.articles.error.title')}</p>
                  <p className="mt-1 text-error">{error}</p>
                  <button type="button" className="mt-4 rounded-xl bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
                    {t('admin.articles.actions.retry')}
                  </button>
                </td>
              </tr>
            )}

            {!isInitialLoading && !error && articles.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center text-secondary">
                  <span className="material-symbols-outlined text-4xl text-outline">article</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.articles.empty.title')}</p>
                  <p className="mt-1">{t('admin.articles.empty.description')}</p>
                  {canCreate && (
                    <button type="button" className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary" onClick={onCreate}>
                      <span className="material-symbols-outlined text-[19px]" aria-hidden="true">add</span>
                      {t('admin.articles.actions.create')}
                    </button>
                  )}
                </td>
              </tr>
            )}

            {!isInitialLoading && articles.map((article) => {
              const isPending = pendingArticleId === article.id;
              return (
                <tr
                  key={article.id}
                  className={`cursor-pointer hover:bg-surface-container-lowest ${isPending ? 'opacity-60' : ''}`}
                  onClick={() => onView(article)}
                >
                  <td className="px-5 py-4 align-top">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-fixed text-primary">article</span>
                      <div className="min-w-0">
                        <p className="max-w-[280px] overflow-hidden font-medium text-on-surface" style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>{article.title}</p>
                        <p className="mt-1 text-[12px] text-secondary">#{article.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 align-top">
                    <p className="max-w-[390px] whitespace-pre-line text-secondary" style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden' }}>
                      {article.content}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 align-top text-secondary">{safeFormatDate(article.createAt)}</td>
                  <td className="whitespace-nowrap px-5 py-4 align-top text-secondary">{safeFormatDate(article.updateAt)}</td>
                  <td className="px-5 py-4 align-top">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-surface-container hover:text-primary disabled:opacity-50"
                        disabled={isPending}
                        aria-label={t('admin.articles.actions.view_label', { title: article.title })}
                        onClick={(event) => { event.stopPropagation(); onView(article); }}
                      >
                        <span className="material-symbols-outlined text-[19px]">visibility</span>
                      </button>
                      {canUpdate && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-primary-fixed hover:text-primary disabled:opacity-50"
                          disabled={isPending}
                          aria-label={t('admin.articles.actions.edit_label', { title: article.title })}
                          onClick={(event) => { event.stopPropagation(); onEdit(article); }}
                        >
                          <span className="material-symbols-outlined text-[19px]">edit</span>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-error hover:bg-error-container/50 disabled:opacity-50"
                          disabled={isPending}
                          aria-label={t('admin.articles.actions.delete_label', { title: article.title })}
                          onClick={(event) => { event.stopPropagation(); onDelete(article); }}
                        >
                          <span className="material-symbols-outlined text-[19px]">delete</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col items-center justify-between gap-3 border-t border-outline-variant bg-white px-4 py-3 text-center sm:flex-row sm:text-left">
        <p className="text-body-sm text-secondary">{t('admin.articles.pagination.summary', { first: firstItem, last: lastItem, total: totalElements })}</p>
        <div className="flex items-center gap-2">
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage <= 1 || isLoading} aria-label={t('admin.articles.pagination.previous')} onClick={() => onPageChange(currentPage - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="min-w-24 text-center font-label-md text-on-surface">{t('admin.articles.pagination.page', { current: currentPage, total: Math.max(totalPages, 1) })}</span>
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage >= totalPages || isLoading} aria-label={t('admin.articles.pagination.next')} onClick={() => onPageChange(currentPage + 1)}>
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ArticleTable;
